import React from "react";
import { consolidationRequest as api } from "../../api/consolidationApi.js";
import HistoricalValidation, { retainedDate, validationName } from "./HistoricalValidation.jsx";
import { PRIMARY, SECONDARY } from "./Fields.jsx";

function useRetainedRead(path, enabled, refreshKey) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [attempt, retry] = React.useReducer(value => value + 1, 0);
  React.useEffect(() => {
    if (!enabled || !path) return undefined;
    const controller = new AbortController();
    setLoading(true); setError(""); setData(null);
    api(path, { signal: controller.signal }).then(value => {
      if (!controller.signal.aborted) setData(value);
    }).catch(failure => {
      if (!controller.signal.aborted) setError(failure.message);
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [path, enabled, refreshKey, attempt]);
  return { data, error, loading, retry };
}

function ReadState({ read }) {
  return <>{read.loading ? <p role="status" className="text-xs">Chargement…</p> : null}
    {read.error ? <div role="alert" className="text-xs text-red-800">{read.error} <button type="button" className={SECONDARY} onClick={read.retry}>Réessayer</button></div> : null}</>;
}

export default function ValidationHistory({ kind, ownerId, revision, timezone, expanded = false }) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const path = ownerId ? `/${kind === "preload" ? "preloads" : "sheets"}/${ownerId}/revisions` : null;
  const visible = expanded || open;
  const list = useRetainedRead(path, visible, revision);
  const detail = useRetainedRead(selected ? `${path}/${selected}` : null, visible && Boolean(selected), ownerId);
  React.useEffect(() => { setSelected(null); }, [ownerId]);
  const current = list.data?.items?.find(item => item.revision === selected);
  if (!ownerId) return null;
  const Container = expanded ? "div" : "details";
  return <Container className={expanded ? "min-w-0" : "min-w-0 rounded-[4px] border border-slate-200 bg-white px-2 py-0.5"} open={expanded ? undefined : open} onToggle={event => {
    if (event.target === event.currentTarget) setOpen(event.currentTarget.open);
  }}>
    {!expanded ? <summary className="cursor-pointer text-sm font-semibold">Historique des validations</summary> : null}
    {visible ? <div className="mt-2 grid min-w-0 gap-2">
      <ReadState read={list} />
      {list.data?.items?.length === 0 ? <p className="text-xs">Aucune validation conservée.</p> : null}
      {list.data?.items?.length ? <div className="flex flex-wrap gap-1" role="group" aria-label="Validations conservées">
        {/* Oldest first: validations read left to right in chronological order. */}
        {[...list.data.items].reverse().map(item => <button key={item.revision} type="button" className={`${selected === item.revision ? PRIMARY : SECONDARY} text-xs`}
          aria-pressed={selected === item.revision} onClick={() => setSelected(item.revision)}>
          {validationName(item.revision)} · {retainedDate(item.validated_at, timezone)}
        </button>)}
      </div> : null}
      {list.data?.items?.length && !current ? <p className="text-xs text-slate-500">Cliquez sur une validation pour afficher son contenu.</p> : null}
      {selected ? <ReadState read={detail} /> : null}
      {selected && detail.data ? <HistoricalValidation key={detail.data.id} detail={detail.data} changes={current?.differences} summary={current?.summary} ganttChanged={current?.gantt_changed} /> : null}
    </div> : null}
  </Container>;
}

export function ConsolidationArchives({ day, timezone, refreshKey }) {
  const [open, setOpen] = React.useState(false);
  const list = useRetainedRead(`/days/${day}/archives`, open, refreshKey);
  return <details className="min-w-0 rounded-[4px] border border-slate-200 bg-white p-2" open={open} onToggle={event => {
    if (event.target === event.currentTarget) setOpen(event.currentTarget.open);
  }}>
    <summary className="cursor-pointer text-sm font-semibold">Consolidations archivées</summary>
    {open ? <div className="mt-2 grid min-w-0 gap-2">
      <ReadState read={list} />
      {list.data?.items?.length === 0 ? <p className="text-xs">Aucune consolidation archivée avec validation.</p> : null}
      {(list.data?.items || []).map(item => <article key={`${item.kind}:${item.id}`} className="grid min-w-0 gap-1 border-t border-slate-200 pt-2 text-xs">
        <h3 className="font-semibold">{item.kind === "sheet" ? "Relevé" : "Préchargement"} · {item.driver_name || item.employee_name || item.label || "Identité non conservée"}{item.truck_number ? ` · Camion ${item.truck_number}` : ""}</h3>
        <p>Archivé : {retainedDate(item.archived_at, timezone)}{item.reason ? ` · ${item.reason}` : ""}</p>
        <ValidationHistory kind={item.kind} ownerId={item.id} revision={item.revision} timezone={timezone} />
      </article>)}
    </div> : null}
  </details>;
}
